import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { DropdownSelectorPoint } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorPoint/DropdownSelectorPoint'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { selectedRegionHasPermission } from 'App/Map/PanelDetails/_utils/selectedRegionHasPermission'
import type { TLngLat, TValuesTimeseriesRegions } from 'App/Map/types'
import { lineChartTooltipFormatter } from 'components/StackedChart/_utils/lineChartTooltipFormatter/lineChartTooltipFormatter'
import type { StackedChartSection, TChartSeries, TValueTypeObject } from 'components/StackedChart/types'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type {
  TPointCategory,
  TSelectedFieldAssetsStoreState,
} from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { isRegionDataEnabledForCharts } from 'utils/useIsRegionDataEnabled'
import { chooseAmongstUnAggHourlyAndDaily } from '../../chooseAmongstUnAggHourlyAndDaily'
import { getColorRulesForCanopySeries } from '../../createContent/_utils/getColorRulesForSeries'
import { generateSeriesForEnvironmentalChart } from '../_utils/generateSeriesForEnvironmentalChart/generateSeriesForEnvironmentalChart'
import { getXDateFormat } from '../_utils/getXDateFormat'
import {
  makeRegionalSeries,
  makeRegionalSeriesFromRegularSeries,
} from '../_utils/makeRegionalSeriesFromRegularSeries'
import { plotOptionsToHideInactiveSeriesHarderMaker } from '../_utils/plotOptionsToHideInactiveSeriesHarderMaker'
import { updateTooltipForValueSource } from '../_utils/tooltipOptionsForValueSourceCharts'

const pointCategory: TPointCategory = 'weatherPoint'
const permissionRequired = 'VIEW_WEATHER_DETAILS'

const checkPermission = () =>
  selectedPropertyHasPermission({ permission: permissionRequired }) ||
  (isUserOnlyAFreeRegionalUser() && selectedRegionHasPermission({ permission: permissionRequired }))

const possibleValueTypesToRequest: VV.DomainTypes.Weather.TTimeseriesValueTypeKeysMerged[] = [
  'humidity_ABOVE',
  'humidity_IN',
  'humidity_BELOW',
]

const getRegionalValuesRequested = ({
  selectedFieldAssets,
  preferredAggregationInterval,
}: {
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  preferredAggregationInterval: {
    preferredAggregationInterval: ReturnType<typeof chooseAmongstUnAggHourlyAndDaily>
  }
}): Partial<routes.Values.Request> => {
  const selectedRegion = selectedFieldAssets.region

  if (!selectedRegion) return {}

  const valuesForRegion = fieldAssetStore.getState().regions?.[selectedRegion]?.valuesTimeseries || []

  const valuesToRequestForRegion = possibleValueTypesToRequest.filter((v) =>
    valuesForRegion.includes(v as TValuesTimeseriesRegions),
  )

  const valuesRequested = valuesToRequestForRegion.reduce(
    (request, valueType) => {
      request[valueType] = preferredAggregationInterval

      return request
    },
    {} as Partial<Record<(typeof possibleValueTypesToRequest)[number], typeof preferredAggregationInterval>>,
  )

  if (isEmpty(valuesRequested)) return {}

  return {
    regions: {
      regionIds: [selectedRegion],
      valuesRequested,
    },
  }
}

const getPointsValuesRequested = ({
  selectedFieldAssets,
  preferredAggregationInterval,
}: {
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  preferredAggregationInterval: {
    preferredAggregationInterval: ReturnType<typeof chooseAmongstUnAggHourlyAndDaily>
  }
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets[pointCategory] || !selectedFieldAssets.property) return {}

  const valuesForPoint =
    fieldAssetStore.getState().properties?.[selectedFieldAssets.property]?.points?.[
      selectedFieldAssets[pointCategory]
    ]?.valuesTimeseries || []

  const valuesToRequest = possibleValueTypesToRequest.filter((v) => valuesForPoint.includes(v))

  const valuesRequested = valuesToRequest.reduce(
    (request, valueType) => {
      request[valueType] = preferredAggregationInterval

      return request
    },
    {} as Partial<Record<(typeof possibleValueTypesToRequest)[number], typeof preferredAggregationInterval>>,
  )

  if (isEmpty(valuesRequested)) return {}

  return {
    points: {
      lngLats: [selectedFieldAssets[pointCategory]],
      valuesRequested,
    },
  }
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.humidity) return {}

  const preferredAggregationInterval = {
    preferredAggregationInterval: chooseAmongstUnAggHourlyAndDaily(),
  }

  const pointsToRequest = getPointsValuesRequested({
    selectedFieldAssets,
    preferredAggregationInterval,
  })

  const showRegionalData = isRegionDataEnabledForCharts()

  const regionsToRequest = showRegionalData
    ? getRegionalValuesRequested({
        selectedFieldAssets,
        preferredAggregationInterval,
      })
    : {}

  return { ...pointsToRequest, ...regionsToRequest }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const commonReturnItems = {
    title: unitConverter.humidityInCanopy().categoryTitleWithoutUnit(),
    fieldAssetPicker: (
      <DropdownSelectorPoint
        pointCategory={pointCategory}
        valuesTimeseriesToFilterOn={possibleValueTypesToRequest}
      />
    ),
    id: 'stackem-humidity',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const showRegionalData = isRegionDataEnabledForCharts()
  const stationLngLat = String(selectedFieldAssets[pointCategory])
  const stationValues = data?.points?.[stationLngLat]?.values
  const hasAboveCanopySensor = !isEmpty(stationValues?.humidity_ABOVE?.[0]?.timeseries)
  const hasInCanopySensor = !isEmpty(stationValues?.humidity_IN?.[0]?.timeseries)
  const hasBelowCanopySensor = !isEmpty(stationValues?.humidity_BELOW?.[0]?.timeseries)
  const selectedProperty = Number(selectedFieldAssets.property)
  const selectedRegion = selectedFieldAssets.region
  const regionValues = data?.regions?.[String(selectedRegion)]?.values
  const hasRegion = showRegionalData && !!selectedRegion
  const series: TChartSeries[] = []

  const colorRules = getColorRulesForCanopySeries({
    hasAboveCanopySensor,
    hasInCanopySensor,
    hasBelowCanopySensor,
    hasRegion,
  })

  const isVirtual =
    fieldAssetStore.getState().properties?.[Number(selectedProperty)]?.points?.[stationLngLat as TLngLat]
      ?.isVirtual

  const isDaily =
    stationValues?.humidity_ABOVE?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    stationValues?.humidity_BELOW?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    stationValues?.humidity_IN?.[0]?.metadata?.aggregationInterval === 'DAILY' ||
    regionValues?.humidity_IN?.[0]?.metadata?.aggregationInterval === 'DAILY'

  const aboveCanopySeries = generateSeriesForEnvironmentalChart({
    hasSensor: hasAboveCanopySensor,
    valueTypeObject: data.points?.[stationLngLat]?.values?.humidity_ABOVE?.[0] as TValueTypeObject,
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.humidity_ABOVE?.[0] as TValueTypeObject,
    unitConverter: unitConverter.humidityAboveCanopy,
    color: colorRules.above[0],
    compareSeasonsColor: colorRules.above[1],
  })

  const inCanopySeries = generateSeriesForEnvironmentalChart({
    hasSensor: hasInCanopySensor,
    valueTypeObject: data.points?.[stationLngLat]?.values?.humidity_IN?.[0] as TValueTypeObject,
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.humidity_IN?.[0] as TValueTypeObject,
    unitConverter: unitConverter.humidityInCanopy,
    color: colorRules.in[0],
    compareSeasonsColor: colorRules.in[1],
  })

  const belowCanopySeries = generateSeriesForEnvironmentalChart({
    hasSensor: hasBelowCanopySensor,
    valueTypeObject: data.points?.[stationLngLat]?.values?.humidity_BELOW?.[0] as TValueTypeObject,
    compareSeasonsValueTypeObject: compareSeasonsData?.points?.[stationLngLat]?.values
      ?.humidity_BELOW?.[0] as TValueTypeObject,
    unitConverter: unitConverter.humidityBelowCanopy,
    color: colorRules.below[0],
    compareSeasonsColor: colorRules.below[1],
  })

  series.push(...aboveCanopySeries, ...inCanopySeries, ...belowCanopySeries)

  let regionSeriesId: string

  if (hasRegion) {
    const regionName = selectedRegion ? fieldAssetStore.getState().regions?.[selectedRegion]?.name : ''

    if (series.length) {
      const regionData = data.regions?.[selectedRegion]?.values?.humidity_IN?.[0] as TValueTypeObject

      const shapedData = (regionData?.timeseries || []).map((ts) => ({
        x: +new Date(ts.timestamp),
        y: unitConverter
          .humidityInCanopy(typeof ts.value === 'object' ? ts.value?.average : ts.value)
          .value(),
        low: isDaily
          ? unitConverter.humidityInCanopy(typeof ts.value === 'object' ? ts.value?.min : null).value()
          : undefined,
        high: isDaily
          ? unitConverter.humidityInCanopy(typeof ts.value === 'object' ? ts.value?.max : null).value()
          : undefined,
      }))

      const baseSeries: TChartSeries = inCanopySeries[0] || aboveCanopySeries[0] || belowCanopySeries[0]

      const regionalSeries = makeRegionalSeriesFromRegularSeries(baseSeries, {
        name: regionName,
        data: shapedData,
        color: colorRules.region,
      })

      regionSeriesId = regionalSeries.id

      series.push(regionalSeries)
    } else {
      const regionData = data?.regions?.[selectedRegion]?.values?.humidity_IN?.[0]
      const seriesName = selectedFieldAssets[pointCategory] ? regionName : undefined

      const regionalSeries =
        showRegionalData && selectedRegion
          ? generateSeriesForEnvironmentalChart({
              valueTypeObject: regionData as TValueTypeObject,
              hasSensor: true,
              unitConverter: unitConverter.humidityInCanopy,
              overrideSeriesName: seriesName,
              color: colorRules.region,
            })
          : []

      regionalSeries.forEach((s) => series.push(makeRegionalSeries(s)))
    }
  }

  const timezone = getTimezoneForSelectedPropertyOrRegion()

  const allForecastMoments = [
    stationValues?.humidity_ABOVE?.[0]?.metadata.forecastStartsAt,
    stationValues?.humidity_IN?.[0]?.metadata.forecastStartsAt,
    stationValues?.humidity_BELOW?.[0]?.metadata.forecastStartsAt,
    regionValues?.humidity_IN?.[0]?.metadata.forecastStartsAt,
  ]
    .filter(Boolean)
    .map((d) => moment.tz(d, timezone))

  const firstForecastTimestamp = allForecastMoments.length ? +moment.min(allForecastMoments) : +new Date()

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig: {
          semiosHighchartsAdditions: {
            id: commonReturnItems.id,
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          plotOptions: plotOptionsToHideInactiveSeriesHarderMaker(),
          series,

          tooltip: {
            xDateFormat: getXDateFormat(isDaily),
            formatter: function (tooltip) {
              let tooltipContents = lineChartTooltipFormatter(this, tooltip, firstForecastTimestamp)

              tooltipContents = updateTooltipForValueSource({
                seriesIds: [
                  aboveCanopySeries[0]?.id,
                  inCanopySeries[0]?.id,
                  belowCanopySeries[0]?.id,
                  regionSeriesId,
                ].filter(Boolean),
                compareSeasonIds: [
                  aboveCanopySeries[1]?.id,
                  inCanopySeries[1]?.id,
                  belowCanopySeries[1]?.id,
                ].filter(Boolean),
                regionSeriesIds: [regionSeriesId].filter(Boolean),
                points: this.points,
                tooltipContents,
                firstForecastTimestamp,
                isVirtual,
              })

              return tooltipContents
            },
          },
        },
      },
    ],
  }
}
